import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const assetTypeLabels = {
  cash_reserve: 'Reserva',
  investment: 'Investimento',
  property: 'Imovel',
  vehicle: 'Veiculo',
  business: 'Negocio',
  other: 'Outro',
};

const liabilityTypeLabels = {
  loan: 'Emprestimo',
  financing: 'Financiamento',
  mortgage: 'Imobiliario',
  tax: 'Tributo',
  personal_debt: 'Divida pessoal',
  other: 'Outro',
  credit_card: 'Cartao',
};

function createAssetForm() {
  return {
    display_name: '',
    asset_type: 'investment',
    liquidity_profile: 'semi_liquid',
    current_value: '',
    cost_basis: '',
    institution_name: '',
    valuation_date: '',
    notes: '',
  };
}

function createLiabilityForm() {
  return {
    display_name: '',
    liability_type: 'loan',
    outstanding_balance: '',
    monthly_payment: '',
    interest_rate_annual: '',
    due_day_of_month: '',
    maturity_date: '',
    institution_name: '',
    notes: '',
  };
}

function formatDate(value) {
  return value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : 'Nao informado';
}

const PersonalFinanceWealthPage = () => {
  const [loading, setLoading] = useState(true);
  const [savingAsset, setSavingAsset] = useState(false);
  const [savingLiability, setSavingLiability] = useState(false);
  const [wealth, setWealth] = useState(null);
  const [assetForm, setAssetForm] = useState(createAssetForm());
  const [liabilityForm, setLiabilityForm] = useState(createLiabilityForm());

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/personal-finance/wealth');
      setWealth(response.data || null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar patrimonio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const saveAsset = async () => {
    try {
      if (!assetForm.display_name.trim()) {
        toast.error('Nome do ativo e obrigatorio');
        return;
      }
      if (!assetForm.current_value) {
        toast.error('Valor atual do ativo e obrigatorio');
        return;
      }

      setSavingAsset(true);
      await axios.post('/api/personal-finance/assets', {
        display_name: assetForm.display_name.trim(),
        asset_type: assetForm.asset_type,
        liquidity_profile: assetForm.liquidity_profile,
        current_value: Number(assetForm.current_value || 0),
        cost_basis: assetForm.cost_basis ? Number(assetForm.cost_basis) : null,
        institution_name: assetForm.institution_name || null,
        valuation_date: assetForm.valuation_date || null,
        notes: assetForm.notes || null,
      });
      toast.success('Ativo criado');
      setAssetForm(createAssetForm());
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar ativo');
    } finally {
      setSavingAsset(false);
    }
  };

  const saveLiability = async () => {
    try {
      if (!liabilityForm.display_name.trim()) {
        toast.error('Nome do passivo e obrigatorio');
        return;
      }
      if (!liabilityForm.outstanding_balance) {
        toast.error('Saldo devedor e obrigatorio');
        return;
      }

      setSavingLiability(true);
      await axios.post('/api/personal-finance/liabilities', {
        display_name: liabilityForm.display_name.trim(),
        liability_type: liabilityForm.liability_type,
        outstanding_balance: Number(liabilityForm.outstanding_balance || 0),
        monthly_payment: liabilityForm.monthly_payment ? Number(liabilityForm.monthly_payment) : null,
        interest_rate_annual: liabilityForm.interest_rate_annual ? Number(liabilityForm.interest_rate_annual) : null,
        due_day_of_month: liabilityForm.due_day_of_month ? Number(liabilityForm.due_day_of_month) : null,
        maturity_date: liabilityForm.maturity_date || null,
        institution_name: liabilityForm.institution_name || null,
        notes: liabilityForm.notes || null,
      });
      toast.success('Passivo criado');
      setLiabilityForm(createLiabilityForm());
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar passivo');
    } finally {
      setSavingLiability(false);
    }
  };

  const summary = wealth?.summary || {};
  const connectedAssets = Array.isArray(wealth?.assets?.connected) ? wealth.assets.connected : [];
  const manualAssets = Array.isArray(wealth?.assets?.manual) ? wealth.assets.manual : [];
  const manualLiabilities = Array.isArray(wealth?.liabilities?.manual) ? wealth.liabilities.manual : [];
  const cardLiabilities = Array.isArray(wealth?.liabilities?.cards) ? wealth.liabilities.cards : [];
  const monthlyTrend = Array.isArray(wealth?.monthly_trend) ? wealth.monthly_trend : [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Patrimonio liquido</p>
            <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : currency.format(Number(summary.net_worth_total || 0))}</p>
            <p className="text-sm text-slate-500">Ativos menos passivos modelados</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Ativos totais</p>
            <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : currency.format(Number(summary.total_assets || 0))}</p>
            <p className="text-sm text-slate-500">Contas conectadas mais ativos manuais</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Passivos totais</p>
            <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : currency.format(Number(summary.total_liabilities || 0))}</p>
            <p className="text-sm text-slate-500">Inclui passivos manuais e cartoes derivados</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Delta vs snapshot anterior</p>
            <p className="text-2xl font-semibold text-slate-900">
              {summary.delta_vs_previous_snapshot === null || summary.delta_vs_previous_snapshot === undefined
                ? '--'
                : currency.format(Number(summary.delta_vs_previous_snapshot || 0))}
            </p>
            <p className="text-sm text-slate-500">
              {summary.previous_snapshot_date ? `Comparado com ${formatDate(summary.previous_snapshot_date)}` : 'Sem historico anterior'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.12fr_0.88fr]">
        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Wealth</p>
                  <h2 className="text-xl font-semibold text-slate-900">Composicao do patrimonio</h2>
                </div>
                <button className="btn-secondary" onClick={loadData} disabled={loading}>Atualizar</button>
              </div>

              <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5">
                <p className="text-sm text-emerald-900">
                  Patrimonio liquido aqui nao e um cadastro avulso: ele combina contas conectadas, ativos manuais, passivos manuais e obrigacoes futuras de cartao.
                </p>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded-2xl border border-emerald-200 bg-white/70 p-4">
                    <p className="text-sm text-emerald-800">Contas conectadas</p>
                    <p className="mt-2 text-xl font-semibold text-slate-900">{currency.format(Number(summary.cash_assets_total || 0))}</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-white/70 p-4">
                    <p className="text-sm text-emerald-800">Ativos manuais</p>
                    <p className="mt-2 text-xl font-semibold text-slate-900">{currency.format(Number(summary.manual_assets_total || 0))}</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-white/70 p-4">
                    <p className="text-sm text-emerald-800">Cartoes e dividas</p>
                    <p className="mt-2 text-xl font-semibold text-slate-900">{currency.format(Number((summary.manual_liabilities_total || 0) + (summary.card_liabilities_total || 0)))}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr_1.05fr]">
                <div className="rounded-3xl border border-slate-200 bg-white p-5">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Tendencia recente</p>
                    <p className="mt-1 text-sm text-slate-500">Ultimo snapshot de cada mes capturado pelo modulo.</p>
                  </div>
                  <div className="mt-4 space-y-3">
                    {monthlyTrend.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                        Sem historico suficiente ainda. Os snapshots passam a nascer neste slice.
                      </div>
                    )}
                    {monthlyTrend.map((item) => (
                      <div key={item.month} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-medium text-slate-900">{item.month}</p>
                            <p className="mt-1 text-sm text-slate-500">Snapshot em {formatDate(item.snapshot_date)}</p>
                          </div>
                          <p className="text-lg font-semibold text-slate-900">{currency.format(Number(item.net_worth_total || 0))}</p>
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm">
                            <p className="text-slate-500">Ativos</p>
                            <p className="mt-1 font-semibold text-slate-900">{currency.format(Number(item.total_assets || 0))}</p>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm">
                            <p className="text-slate-500">Passivos</p>
                            <p className="mt-1 font-semibold text-slate-900">{currency.format(Number(item.total_liabilities || 0))}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-5">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Base conectada do patrimonio</p>
                    <p className="mt-1 text-sm text-slate-500">Contas que ja entram automaticamente no patrimonio liquido.</p>
                  </div>
                  <div className="mt-4 space-y-3">
                    {connectedAssets.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                        Nenhuma conta conectada elegivel para patrimonio ainda.
                      </div>
                    )}
                    {connectedAssets.map((item) => (
                      <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-slate-900">{item.display_name}</p>
                            <p className="mt-1 text-sm text-slate-500">{item.institution_name || assetTypeLabels[item.asset_type] || item.asset_type}</p>
                          </div>
                          <span className="badge-info">{assetTypeLabels[item.asset_type] || item.asset_type}</span>
                        </div>
                        <p className="mt-3 text-lg font-semibold text-slate-900">{currency.format(Number(item.current_value || 0))}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="card">
              <div className="card-body space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Ativos manuais</p>
                  <h2 className="text-xl font-semibold text-slate-900">Fora das contas conectadas</h2>
                </div>
                <div className="space-y-3">
                  {manualAssets.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      Nenhum ativo manual cadastrado.
                    </div>
                  )}
                  {manualAssets.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-900">{item.display_name}</p>
                          <p className="mt-1 text-sm text-slate-500">
                            {assetTypeLabels[item.asset_type] || item.asset_type} · {item.institution_name || 'Sem instituicao'}
                          </p>
                        </div>
                        <span className="badge-info">{item.liquidity_profile}</span>
                      </div>
                      <p className="mt-3 text-lg font-semibold text-slate-900">{currency.format(Number(item.current_value || 0))}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Passivos</p>
                  <h2 className="text-xl font-semibold text-slate-900">Dividas modeladas</h2>
                </div>
                <div className="space-y-3">
                  {manualLiabilities.length === 0 && cardLiabilities.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      Nenhum passivo modelado ainda.
                    </div>
                  )}
                  {manualLiabilities.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-900">{item.display_name}</p>
                          <p className="mt-1 text-sm text-slate-500">{liabilityTypeLabels[item.liability_type] || item.liability_type}</p>
                        </div>
                        <span className="badge-warning">manual</span>
                      </div>
                      <p className="mt-3 text-lg font-semibold text-slate-900">{currency.format(Number(item.outstanding_balance || 0))}</p>
                    </div>
                  ))}
                  {cardLiabilities.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-900">{item.display_name}</p>
                          <p className="mt-1 text-sm text-slate-500">{item.notes}</p>
                        </div>
                        <span className="badge-info">cartao</span>
                      </div>
                      <p className="mt-3 text-lg font-semibold text-slate-900">{currency.format(Number(item.outstanding_balance || 0))}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Novo ativo</p>
                <h2 className="text-xl font-semibold text-slate-900">Expandir patrimonio</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="form-label">Nome</label>
                  <input className="form-input" value={assetForm.display_name} onChange={(event) => setAssetForm((current) => ({ ...current, display_name: event.target.value }))} placeholder="Tesouro, carro, imovel, participacao..." />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="form-label">Tipo</label>
                    <select className="form-select" value={assetForm.asset_type} onChange={(event) => setAssetForm((current) => ({ ...current, asset_type: event.target.value }))}>
                      {Object.entries(assetTypeLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Liquidez</label>
                    <select className="form-select" value={assetForm.liquidity_profile} onChange={(event) => setAssetForm((current) => ({ ...current, liquidity_profile: event.target.value }))}>
                      <option value="liquid">Liquido</option>
                      <option value="semi_liquid">Semi-liquido</option>
                      <option value="illiquid">Iliquido</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="form-label">Valor atual</label>
                    <input className="form-input" type="number" min="0" step="0.01" value={assetForm.current_value} onChange={(event) => setAssetForm((current) => ({ ...current, current_value: event.target.value }))} />
                  </div>
                  <div>
                    <label className="form-label">Custo base</label>
                    <input className="form-input" type="number" min="0" step="0.01" value={assetForm.cost_basis} onChange={(event) => setAssetForm((current) => ({ ...current, cost_basis: event.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="form-label">Instituicao</label>
                    <input className="form-input" value={assetForm.institution_name} onChange={(event) => setAssetForm((current) => ({ ...current, institution_name: event.target.value }))} />
                  </div>
                  <div>
                    <label className="form-label">Data de valoracao</label>
                    <input className="form-input" type="date" value={assetForm.valuation_date} onChange={(event) => setAssetForm((current) => ({ ...current, valuation_date: event.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="form-label">Notas</label>
                  <textarea className="form-textarea min-h-[110px]" value={assetForm.notes} onChange={(event) => setAssetForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Premissas de valoracao, liquidez, observacoes..." />
                </div>
              </div>

              <button className="btn-primary w-full" onClick={saveAsset} disabled={savingAsset}>
                {savingAsset ? 'Salvando...' : 'Criar ativo'}
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Novo passivo</p>
                <h2 className="text-xl font-semibold text-slate-900">Mapear dividas</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="form-label">Nome</label>
                  <input className="form-input" value={liabilityForm.display_name} onChange={(event) => setLiabilityForm((current) => ({ ...current, display_name: event.target.value }))} placeholder="Financiamento, emprestimo, parcelamento..." />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="form-label">Tipo</label>
                    <select className="form-select" value={liabilityForm.liability_type} onChange={(event) => setLiabilityForm((current) => ({ ...current, liability_type: event.target.value }))}>
                      {Object.entries(liabilityTypeLabels).filter(([value]) => value !== 'credit_card').map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Saldo devedor</label>
                    <input className="form-input" type="number" min="0" step="0.01" value={liabilityForm.outstanding_balance} onChange={(event) => setLiabilityForm((current) => ({ ...current, outstanding_balance: event.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="form-label">Parcela mensal</label>
                    <input className="form-input" type="number" min="0" step="0.01" value={liabilityForm.monthly_payment} onChange={(event) => setLiabilityForm((current) => ({ ...current, monthly_payment: event.target.value }))} />
                  </div>
                  <div>
                    <label className="form-label">Juros a.a.</label>
                    <input className="form-input" type="number" min="0" step="0.0001" value={liabilityForm.interest_rate_annual} onChange={(event) => setLiabilityForm((current) => ({ ...current, interest_rate_annual: event.target.value }))} />
                  </div>
                  <div>
                    <label className="form-label">Dia do vencimento</label>
                    <input className="form-input" type="number" min="1" max="31" value={liabilityForm.due_day_of_month} onChange={(event) => setLiabilityForm((current) => ({ ...current, due_day_of_month: event.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="form-label">Instituicao</label>
                    <input className="form-input" value={liabilityForm.institution_name} onChange={(event) => setLiabilityForm((current) => ({ ...current, institution_name: event.target.value }))} />
                  </div>
                  <div>
                    <label className="form-label">Data final</label>
                    <input className="form-input" type="date" value={liabilityForm.maturity_date} onChange={(event) => setLiabilityForm((current) => ({ ...current, maturity_date: event.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="form-label">Notas</label>
                  <textarea className="form-textarea min-h-[110px]" value={liabilityForm.notes} onChange={(event) => setLiabilityForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Origem da divida, renegociacao, garantia, observacoes..." />
                </div>
              </div>

              <button className="btn-primary w-full" onClick={saveLiability} disabled={savingLiability}>
                {savingLiability ? 'Salvando...' : 'Criar passivo'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceWealthPage;
